import { Temporal } from '@js-temporal/polyfill';
import type { CareState, DoseOccurrence, Episode, Medication, Schedule } from './types.ts';

export function scheduleDescription(schedule: Schedule): string {
  if(schedule.type==='prn')return 'As needed';
  if(schedule.type==='interval')return `Every ${schedule.hours} hours`;
  const times=schedule.times.map(t=>{const [h,m]=t.split(':').map(Number);return `${h%12||12}:${String(m).padStart(2,'0')} ${h>=12?'PM':'AM'}`;}).join(' · ');
  if(schedule.type==='daily')return `Every day at ${times}`;
  const frequency=schedule.every===1?`Every ${schedule.unit.slice(0,-1)}`:`Every ${schedule.every} ${schedule.unit}`;
  const anchor=Temporal.PlainDate.from(schedule.anchorDate);
  const day=schedule.unit==='weeks'?` on ${['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'][anchor.dayOfWeek-1]}`:schedule.unit==='months'?` on day ${anchor.day}`:'';
  return `${frequency}${day} at ${times}`;
}

const instant = (value: Date | string): Temporal.Instant => Temporal.Instant.from(value instanceof Date ? value.toISOString() : value);

/** Resolve a wall-clock slot without silently dropping a skipped or repeated time. */
export function resolveDailyTime(date: string, time: string, timezone: string): { scheduledAt: string; adjustment?: string } {
  const requested = Temporal.PlainDateTime.from(`${date}T${time}:00`);
  const fields = { timeZone: timezone, year: requested.year, month: requested.month, day: requested.day, hour: requested.hour, minute: requested.minute };
  const first = Temporal.ZonedDateTime.from(fields, { disambiguation: 'earlier' });
  const later = Temporal.ZonedDateTime.from(fields, { disambiguation: 'later' });
  if (first.toPlainDateTime().equals(requested)) {
    return { scheduledAt: first.toInstant().toString(), ...(first.epochMilliseconds !== later.epochMilliseconds ? { adjustment: 'First occurrence of repeated local time' } : {}) };
  }
  // A gap can be a whole skipped calendar day. Find the first valid minute.
  let candidate = requested;
  for (let minutes = 0; minutes <= 1_440; minutes++) {
    const resolved = Temporal.ZonedDateTime.from({ timeZone: timezone, year: candidate.year, month: candidate.month, day: candidate.day, hour: candidate.hour, minute: candidate.minute }, { disambiguation: 'earlier' });
    if (resolved.toPlainDateTime().equals(candidate)) return { scheduledAt: resolved.toInstant().toString(), adjustment: `Shifted to first valid local time (${resolved.toPlainTime().toString({ smallestUnit: 'minute' })})` };
    candidate = candidate.add({ minutes: 1 });
  }
  throw new Error('Could not resolve this medication time.');
}

export function getOccurrences(state: CareState, dogId: string, from: Date | string, to: Date | string, now: Date | string = new Date()): DoseOccurrence[] {
  const fromMs = instant(from).epochMilliseconds;
  const toMs = instant(to).epochMilliseconds;
  const nowMs = instant(now).epochMilliseconds;
  if (toMs <= fromMs) return [];
  if (toMs - fromMs > 370 * 86_400_000) throw new Error('Choose a date range of one year or less.');
  const result: DoseOccurrence[] = [];
  for (const medication of state.medications.filter(m => m.dogId === dogId && m.schedule.type !== 'prn')) {
    const startMs = Math.max(fromMs, instant(medication.startsAt).epochMilliseconds);
    const endMs = Math.min(toMs, medication.endsAt ? instant(medication.endsAt).epochMilliseconds : toMs);
    if (endMs <= startMs) continue;
    const add = (scheduledAt: string, slot?: string, adjustment?: string) => {
      const at = instant(scheduledAt).epochMilliseconds;
      if (at < startMs || at >= endMs) return;
      const id = `${medication.id}:${scheduledAt}${slot ? `:${slot}` : ''}`;
      const administrations = state.administrations.filter(a => a.occurrenceId === id && a.status !== 'voided');
      const given = administrations.filter(a => a.status === 'given');
      const status = (given.length > 1 && given.some(a => a.reviewResolution !== 'additional-dose-confirmed')) || administrations.some(a => a.needsReview) ? 'review' : given.length ? 'given' : administrations.some(a => a.status === 'missed') ? 'missed' : at <= nowMs ? 'due' : 'upcoming';
      result.push({ id, medication, scheduledAt, adjustment, administrations, status });
    };
    if (medication.schedule.type === 'interval') {
      const step = medication.schedule.hours * 3_600_000;
      if (!Number.isFinite(step) || step <= 0) continue;
      const anchor = instant(medication.schedule.anchor).epochMilliseconds;
      for (let at = anchor + Math.max(0, Math.ceil((startMs - anchor) / step)) * step; at < endMs; at += step) add(Temporal.Instant.fromEpochMilliseconds(at).toString());
    } else if (medication.schedule.type === 'daily' || medication.schedule.type === 'calendar') {
      const schedule=medication.schedule;
      const anchor=schedule.type==='calendar'?Temporal.PlainDate.from(schedule.anchorDate):null;
      let date = Temporal.Instant.fromEpochMilliseconds(startMs).toZonedDateTimeISO(medication.timezone).toPlainDate().subtract({ days: 1 });
      const last = Temporal.Instant.fromEpochMilliseconds(endMs).toZonedDateTimeISO(medication.timezone).toPlainDate();
      while (Temporal.PlainDate.compare(date, last) <= 0) {
        let scheduledDay=true;
        if(schedule.type==='calendar'&&anchor){
          const days=anchor.until(date,{largestUnit:'days'}).days;
          if(days<0)scheduledDay=false;
          else if(schedule.unit==='months'){
            const months=(date.year-anchor.year)*12+date.month-anchor.month;
            scheduledDay=months%schedule.every===0&&anchor.add({months}).equals(date);
          }else scheduledDay=days%(schedule.every*(schedule.unit==='weeks'?7:1))===0;
        }
        for (const slot of scheduledDay?[...new Set(schedule.times)].sort():[]) {
          const resolved = resolveDailyTime(date.toString(), slot, medication.timezone);
          add(resolved.scheduledAt, slot, resolved.adjustment);
        }
        date = date.add({ days: 1 });
      }
    }
  }
  return result.sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt) || a.medication.name.localeCompare(b.medication.name) || a.id.localeCompare(b.id));
}

export function getEpisodeDuration(episode: Episode, now: Date | string = new Date()): number | undefined {
  if (episode.durationUnknown) return undefined;
  if (episode.stoppedAt && episode.durationSeconds !== undefined) return Math.max(0, episode.durationSeconds);
  const end = episode.stoppedAt ? new Date(episode.stoppedAt).getTime() : new Date(now).getTime();
  return Math.max(0, Math.floor((end - new Date(episode.startedAt).getTime()) / 1_000));
}

export function medicationIsCurrent(medication: Medication, now: Date | string = new Date()): boolean {
  const at = new Date(now).getTime();
  return new Date(medication.startsAt).getTime() <= at && (!medication.endsAt || new Date(medication.endsAt).getTime() > at);
}
